import { FormHelperText, TextField as MuiTextField } from '@mui/material';
import { styled } from 'styled-components';
import theme from 'styled-theming';

import Icon from 'components/Icon';
import { hideVisually } from 'style/components/A11Y';
import { styleBodyL, styleBodyM, styleBodyS, styleBodySSpaced, styleLabelM } from 'style/components/Typography';
import { MQ } from 'style/mediaQueries';

const labelFocus = theme('theme', {
    undefined: '--text-on-neutral-secondary',
    lightgray: '--text-on-neutral-secondary',
    blue: '--text-on-blue-secondary',
    cyan: '--text-on-cyan-secondary',
    black: '--text-on-blue-secondary',
});

const textPlaceholder = theme('theme', {
    undefined: '--text-on-neutral-disabled',
    lightgray: '--text-on-neutral-disabled',
    blue: '--text-on-blue-disabled',
    cyan: '--text-on-cyan-disabled',
    black: '--text-on-blue-disabled',
});

const textDisabled = theme('theme', {
    undefined: '--text-on-neutral-disabled',
    lightgray: '--text-on-neutral-disabled',
    blue: '--text-on-blue-disabled',
    cyan: '--text-on-cyan-disabled',
    black: '--text-on-blue-disabled',
});

const textError = theme('theme', {
    undefined: '--text-on-neutral-inverted',
    lightgray: '--text-on-neutral-inverted',
    blue: '--text-on-coral-primary',
    cyan: '--text-on-neutral-inverted',
    black: '--text-on-coral-primary',
});

const textColor = theme('theme', {
    undefined: '--text-on-neutral-primary',
    lightgray: '--text-on-neutral-primary',
    blue: '--text-on-blue-primary',
    cyan: '--text-on-cyan-primary',
    black: '--text-on-blue-primary',
});

const borderColor = theme('theme', {
    undefined: '--field-on-neutral-default-border',
    lightgray: '--field-on-neutral-default-border',
    blue: '--field-on-blue-default-border',
    cyan: '--field-on-cyan-default-border',
    black: '--field-on-blue-default-border',
});

const borderHover = theme('theme', {
    undefined: '--field-on-neutral-hover-border',
    lightgray: '--field-on-neutral-hover-border',
    blue: '--field-on-blue-hover-border',
    cyan: '--field-on-cyan-hover-border',
    black: '--field-on-blue-hover-border',
});

const borderError = theme('theme', {
    undefined: '--field-on-neutral-error-border',
    lightgray: '--field-on-neutral-error-border',
    blue: '--field-on-blue-error-border',
    cyan: '--field-on-cyan-error-border',
    black: '--field-on-blue-error-border',
});

const colorSuccess = theme('theme', {
    undefined: '--color-green',
    lightgray: '--color-green',
    blue: '--color-traffic-green',
    cyan: '--color-green',
    black: '--color-green',
});

const borderDisabled = theme('theme', {
    undefined: '--field-on-neutral-disabled-border',
    lightgray: '--field-on-neutral-disabled-border',
    blue: '--field-on-blue-disabled-border',
    cyan: '--field-on-cyan-disabled-border',
    black: '--field-on-blue-disabled-border',
});

export const FieldWrapper = styled.div`
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    gap: 0;
    grid-column: col-start 1 / span 2;
    width: 100%;

    ${MQ.FROM_M} {
        grid-column: ${theme('displayOption', {
            Full: 'auto / span 6',
            Half: 'auto / span 3',
            OneThird: 'auto / span 2',
            TwoThirds: 'auto / span 4',
        })};
    }
`;

export const StyledTextField = styled(MuiTextField).attrs<{
    $isValidated?: boolean;
}>(() => ({}))`
    && {
        width: 100%;

        .MuiInputLabel {
            &-root {
                ${theme('size', {
                    small: styleBodyM,
                    medium: styleBodyL,
                })};
                transform: ${theme('size', {
                    small: 'translate(0, 0.875rem)',
                    medium: 'translate(0, 1.3125rem)',
                })};
                padding: 0 0 0 0.875rem;
                color: var(${textColor});

                ${MQ.FROM_XL} {
                    transform: ${theme('size', {
                        small: 'translate(0, 1.3125rem)',
                        medium: 'translate(0, 1.75rem)',
                    })};
                    padding: ${theme('size', {
                        small: '0 0 0 0.875rem',
                        medium: '0 0 0 1.3125rem',
                    })};
                }

                &.Mui-error {
                    padding-right: 1.3125rem;

                    ${MQ.FROM_XL} {
                        padding-right: 1.3125rem;
                    }
                }

                &.Mui-adornment {
                    padding-right: 1.3125rem;

                    ${MQ.FROM_XL} {
                        padding-right: 1.3125rem;
                    }
                }

                &.Mui-error.Mui-adornment {
                    padding-right: 3.5rem;

                    ${MQ.FROM_XL} {
                        padding-right: 3.9375rem;
                    }
                }

                &.Mui-focused {
                    ${theme('size', {
                        small: styleBodySSpaced,
                        medium: styleBodyM,
                    })};
                    transform: ${theme('size', {
                        small: 'translate(0, 0)',
                        medium: 'translate(0, 0.4375rem)',
                    })};
                    color: var(${labelFocus});

                    ${MQ.FROM_XL} {
                        transform: ${theme('size', {
                            small: 'translate(0, 0.4375rem)',
                            medium: 'translate(0, 0.875rem)',
                        })};
                    }
                }

                &.Mui-disabled {
                    color: var(${textDisabled});
                }
            }

            &-shrink {
                ${theme('size', {
                    small: styleBodySSpaced,
                    medium: styleBodyM,
                })};
                transform: ${theme('size', {
                    small: 'translate(0, 0)',
                    medium: 'translate(0, 0.4375rem)',
                })};
                color: var(${labelFocus});

                ${MQ.FROM_XL} {
                    transform: ${theme('size', {
                        small: 'translate(0, 0.4375rem)',
                        medium: 'translate(0, 0.875rem)',
                    })};
                }

                /* prettier-ignore */
                &:has(+ .MuiOutlinedInput-root
                        .MuiInputBase-input:-webkit-autofill) {
                    color: var(--color-black);
                }
            }
        }

        .MuiInputBase {
            &-input {
                ${theme('size', {
                    small: styleBodyM,
                    medium: styleBodyL,
                })};
                padding: ${theme('size', {
                    small: '1.75rem 0.875rem 0rem',
                    medium: '2.1875rem 0.875rem 0.4375rem',
                })};
                height: 1.75rem;
                color: var(${textColor});

                ${MQ.FROM_XL} {
                    padding: ${theme('size', {
                        small: '2.1875rem 0.875rem 0.4375rem',
                        medium: '2.625rem 1.3125rem 0.875rem',
                    })};
                    height: ${theme('size', {
                        small: '1.75rem',
                        medium: '2.1875rem',
                    })};
                }

                &.Mui-disabled {
                    -webkit-text-fill-color: var(${textDisabled});
                    color: var(${textDisabled});
                }

                &::placeholder {
                    opacity: 1;
                    color: var(${textPlaceholder});
                }
            }

            &-root {
                padding-right: 0;

                .MuiOutlinedInput {
                    &-notchedOutline {
                        top: 0;
                        transition: border-color 0.2s ease-in-out;
                        border-color: ${(props) => (props.$isValidated ? `var(${colorSuccess(props)})` : `var(${borderColor(props)})`)};

                        legend {
                            display: block;
                            width: 0;
                            height: 0;
                            overflow: hidden;
                        }
                    }
                }

                &:hover {
                    &:not(.Mui-disabled, .Mui-error) {
                        .MuiOutlinedInput {
                            &-notchedOutline {
                                border-color: ${(props) =>
                                    props.$isValidated ? `var(${colorSuccess(props)})` : `var(${borderHover(props)})`};
                            }
                        }
                    }
                }

                &.Mui-error.Mui-focused,
                &.Mui-error {
                    .MuiOutlinedInput {
                        &-notchedOutline {
                            border-color: var(${borderError});
                            border-top-left-radius: 0;
                        }
                    }
                }

                &.Mui-focused {
                    .MuiOutlinedInput {
                        &-notchedOutline {
                            box-shadow: 0 0 0 0.4375rem var(--field-focus);
                            border-width: 1px;
                            border-color: ${(props) => (props.$isValidated ? `var(${colorSuccess(props)})` : `var(${borderHover(props)})`)};
                        }
                    }
                }

                &.Mui-disabled {
                    .MuiOutlinedInput {
                        &-notchedOutline {
                            border-color: var(${borderDisabled});
                        }
                    }
                }
            }
        }

        .MuiFormHelperText {
            &-root {
                ${theme('size', {
                    small: styleBodyS,
                    medium: styleBodyM,
                })};
                margin: 0.4375rem 0 0;
                color: var(${labelFocus});

                &.Mui-disabled {
                    color: var(${textDisabled});
                }
            }
        }
    }
`;

export const AdornmentWrapper = styled.div`
    ${theme('size', {
        small: styleBodyM,
        medium: styleBodyL,
    })};
    align-items: center;
    color: var(${textPlaceholder});
    display: flex;
    flex-direction: row;
    gap: 0.875rem;
    height: 100%;
    padding-right: 0.875rem;

    ${MQ.FROM_XL} {
        gap: ${theme('size', {
            small: '0.875rem',
            medium: '1.3125rem',
        })};
        padding-right: ${theme('size', {
            small: '0.875rem',
            medium: '1.3125rem',
        })};
    }
`;

export const SuccessIcon = styled(Icon)`
    color: var(${colorSuccess});
`;

export const ErrorIcon = styled(Icon)`
    color: var(${borderError});
`;

export const ErrorText = styled(FormHelperText)`
    && {
        ${styleLabelM};
        z-index: 2;
        margin: 0 auto 0 0;

        span {
            display: block;
            margin-right: 2.625rem;
            border-radius: 0.25rem 0.25rem 0 0;
            background-color: var(${borderError});
            padding: 0 0.4375rem;
            color: var(${textError});
        }
    }
`;

export const HelperButton = styled.span`
    display: flex;
`;

export const HelperIcon = styled(Icon)`
    color: var(${textColor});
`;

export const HelperTooltipText = styled.div`
    ${styleBodyS};
    background-color: var(--color-black-l95);
    border-radius: 0.25rem;
    box-shadow: 0 12px 28px rgb(0 0 0 / 5%);
    color: var(--color-black);
    cursor: default;
    padding: 0.875rem;
    position: absolute;
    right: 0;
    top: calc(100% + 0.5rem);
    transition: opacity 0.2s ease-in-out;
    user-select: none;
    width: 12.375rem;
    z-index: 100;

    &::before {
        position: absolute;
        top: -0.25rem;
        right: 0.5625rem;
        transform: rotate(45deg);
        background-color: var(--color-black-l95);
        width: 0.5rem;
        height: 0.5rem;
        content: '';
    }
`;

export const HelperTooltip = styled.div`
    cursor: pointer;
    position: relative;

    /* no-js toggle function */
    &:focus-within {
        ${HelperButton} {
            pointer-events: none;
        }

        ${HelperIcon} {
            color: var(${borderHover});
        }

        ${HelperTooltipText} {
            opacity: 1;
        }
    }

    &:not(:focus-within) {
        ${HelperTooltipText} {
            ${hideVisually};
            opacity: 0;
        }
    }
`;
